import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { useProfile } from './ProfileContext'
import { useWebSocketOptional, NotificationPayload } from './WebSocketContext'
import { api } from '../services/api'

interface SidebarBadges {
  // Shipper badges
  pendingOffers: number
  // Freight Forwarder badges
  activeOpportunities: number
  pendingInvites: number
  // Generic notification count
  unreadNotifications: number
}

interface SidebarBadgeContextType {
  badges: SidebarBadges
  loading: boolean
  refreshBadges: () => Promise<void>
  isWebSocketConnected: boolean
}

const defaultBadges: SidebarBadges = {
  pendingOffers: 0,
  activeOpportunities: 0,
  pendingInvites: 0,
  unreadNotifications: 0,
}

const SidebarBadgeContext = createContext<SidebarBadgeContextType>({
  badges: defaultBadges,
  loading: false,
  refreshBadges: async () => {},
  isWebSocketConnected: false,
})

export function SidebarBadgeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { currentProfile } = useProfile()
  const webSocket = useWebSocketOptional()
  const [badges, setBadges] = useState<SidebarBadges>(defaultBadges)
  const [loading, setLoading] = useState(false)

  const fetchBadges = useCallback(async () => {
    if (!user || !currentProfile) return

    // Verificar se o usuário realmente tem uma empresa do tipo correto
    const hasShipper = user.companies?.some(c => c.type === 'shipper')
    const hasFreightForwarder = user.companies?.some(c => c.type === 'freight-forwarder')

    setLoading(true)
    try {
      if (currentProfile === 'shipper' && hasShipper) {
        const response = await api.get('/shipper/stats')
        setBadges(prev => ({
          ...prev,
          pendingOffers: response.data.stats?.pendingOffers || 0,
        }))
      } else if (currentProfile === 'freight-forwarder' && hasFreightForwarder) {
        const [statsResponse, invitesResponse] = await Promise.all([
          api.get('/freight-forwarder/stats'),
          api.get('/freight-forwarder/invites/count'),
        ])
        setBadges(prev => ({
          ...prev,
          activeOpportunities: statsResponse.data.stats?.activeOpportunities || 0,
          pendingInvites: invitesResponse.data.count || 0,
        }))
      }
    } catch (error) {
      console.error('Failed to fetch sidebar badges:', error)
    } finally {
      setLoading(false)
    }
  }, [user, currentProfile])

  // Fetch badges when user or profile changes
  useEffect(() => {
    fetchBadges()
  }, [fetchBadges])

  // Handle real-time notifications via WebSocket
  useEffect(() => {
    if (!webSocket?.onNotification) return

    const unsubscribe = webSocket.onNotification((notification: NotificationPayload) => {
      console.log('[SidebarBadge] Real-time notification received:', notification.type)

      // Update badge counts based on notification type
      setBadges(prev => {
        switch (notification.type) {
          case 'new_quote':
            // New quote available for freight forwarders
            return { ...prev, activeOpportunities: prev.activeOpportunities + 1 }

          case 'new_offer':
            // New offer for shippers
            return { ...prev, pendingOffers: prev.pendingOffers + 1 }

          case 'shipper_invite':
            // New invite for freight forwarders
            return { ...prev, pendingInvites: prev.pendingInvites + 1 }

          default:
            // For other notification types, increment generic counter
            return { ...prev, unreadNotifications: prev.unreadNotifications + 1 }
        }
      })
    })

    return unsubscribe
  }, [webSocket])

  // Polling fallback - only when WebSocket is NOT connected
  // When connected, we rely on real-time updates
  useEffect(() => {
    // If WebSocket is connected, use longer polling interval as backup
    // If not connected, use shorter interval
    const pollInterval = webSocket?.isConnected ? 120000 : 60000 // 2 min if connected, 1 min if not

    const interval = setInterval(() => {
      fetchBadges()
    }, pollInterval)

    return () => clearInterval(interval)
  }, [fetchBadges, webSocket?.isConnected])

  const refreshBadges = useCallback(async () => {
    await fetchBadges()
  }, [fetchBadges])

  return (
    <SidebarBadgeContext.Provider value={{
      badges,
      loading,
      refreshBadges,
      isWebSocketConnected: webSocket?.isConnected ?? false
    }}>
      {children}
    </SidebarBadgeContext.Provider>
  )
}

export function useSidebarBadges() {
  const context = useContext(SidebarBadgeContext)
  if (!context) {
    throw new Error('useSidebarBadges must be used within a SidebarBadgeProvider')
  }
  return context
}
